import {
  alpha,
  Badge,
  Box,
  Divider,
  IconButton,
  List,
  ListItem,
  Popover,
  Tooltip,
  Typography,
  CircularProgress,
  Button
} from "@mui/material";
import { useRef, useState, useEffect } from "react";
import NotificationsActiveTwoToneIcon from "@mui/icons-material/NotificationsActiveTwoTone";
import { styled } from "@mui/material/styles";

import { useNotifications } from "src/contexts/NotificationContext";
import { NotificationReviewDialog } from "src/content/applications/Users/dashboardNotifications/NotificationReviewDialog";
import { NotificationCommunicationDialog } from "src/content/applications/Users/dashboardNotifications/NotificationCommunicationDialog";
import { enqueueSnackbar } from "notistack";

interface NotificationItem {
  id: number;
  title: string;
  message: string;
  referenceType: string;
  referenceId: number;
  createdAt: string;
}

const NotificationsBadge = styled(Badge)(
  ({ theme }) => `
    .MuiBadge-badge {
        background-color: ${alpha(theme.palette.error.main, 0.1)};
        color: ${theme.palette.error.main};
        min-width: 16px;
        height: 16px;
        padding: 0;
    }
`
);

function HeaderNotifications() {
  const ref = useRef<any>(null);
  const [isOpen, setOpen] = useState(false);
  const [loading] = useState(false);
  const { notifications, unreadCount, markAsRead, markAllAsRead } = useNotifications();

  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [selectedServiceOrderId, setSelectedServiceOrderId] = useState<number | null>(null);
  const [communicationDialogOpen, setCommunicationDialogOpen] = useState(false);
  const [selectedCommunicationId, setSelectedCommunicationId] = useState<number | null>(null);

  useEffect(() => {
    const handleOpenReview = (event: any) => {
      setSelectedServiceOrderId(event.detail.serviceOrderId);
      setReviewDialogOpen(true);
    };

    const handleOpenCommunication = (event: any) => {
      setSelectedCommunicationId(event.detail.communicationId);
      setCommunicationDialogOpen(true);
    };

    window.addEventListener('openReviewDialog', handleOpenReview);
    window.addEventListener('openCommunicationDialog', handleOpenCommunication);

    return () => {
      window.removeEventListener('openReviewDialog', handleOpenReview);
      window.removeEventListener('openCommunicationDialog', handleOpenCommunication);
    };
  }, []);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const formatNotificationTime = (utcDate: string) => {
    if (!utcDate) return "";
    const parsedDate = new Date(utcDate.endsWith("Z") ? utcDate : utcDate + "Z");
    const diffMs = Date.now() - parsedDate.getTime();
    if (diffMs < 0) return "now";

    const diffSec = Math.floor(diffMs / 1000);
    const diffMin = Math.floor(diffSec / 60);
    const diffHr = Math.floor(diffMin / 60);
    const diffDay = Math.floor(diffHr / 24);

    if (diffSec < 60) return `${diffSec}s ago`;
    if (diffMin < 60) return `${diffMin}m ago`;
    if (diffHr < 24) return `${diffHr}h ago`;
    return `${diffDay}d ago`;
  };

  const handleNotificationClick = async (notification: NotificationItem) => {
    try {
      await markAsRead(notification.id);

      const referenceType = notification.referenceType?.toLowerCase();

      if (referenceType === "review" && notification.referenceId) {
        setSelectedServiceOrderId(notification.referenceId);
        setReviewDialogOpen(true);
      } 
      else if (referenceType === "customercommunication" && notification.referenceId) {
        setSelectedCommunicationId(notification.referenceId);
        setCommunicationDialogOpen(true);
      }

      setOpen(false);
    } catch (error) {
      console.error("Error handling notification click:", error);
    }
  };

  const handleMarkAsAllRead = async () => {
    try {
      await markAllAsRead()

      enqueueSnackbar('All notifications are read.', {
        variant: 'success'
      })

    } catch (error) {
      enqueueSnackbar(`Error, while reading notification.`, {
        variant: 'error'
      })
      console.error('error', error)
    }
  }

  return (
    <>
      <Tooltip arrow title="Notifications">
        <IconButton color="primary" ref={ref} onClick={handleOpen}>
          <NotificationsBadge
            badgeContent={unreadCount}
            anchorOrigin={{ vertical: "top", horizontal: "right" }}
          >
            <NotificationsActiveTwoToneIcon />
          </NotificationsBadge>
        </IconButton>
      </Tooltip>

      <Popover
        anchorEl={ref.current}
        onClose={handleClose}
        open={isOpen}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Box sx={{ minWidth: 380 }}>
          <Box sx={{ p: 2, display: 'flex', alignContent: 'center', justifyContent: 'space-between' }}>
            <Typography sx={{ alignContent: 'center' }} variant="h5">Notifications</Typography>
            <Button
              variant="contained"
              color="primary"
              onClick={handleMarkAsAllRead}>Mark as All Read</Button>
          </Box>
          <Divider />

          <List sx={{ p: 0, maxHeight: 400, overflowY: "auto" }}>
            {loading ? (
              <Box p={2} textAlign="center">
                <CircularProgress size={20} />
              </Box>
            ) : notifications.length === 0 ? (
              <ListItem sx={{ p: 2, justifyContent: "center" }}>
                <Typography variant="body2" color="text.secondary">
                  No notifications
                </Typography>
              </ListItem>
            ) : (
              notifications.map((n) => (
                <ListItem
                  key={n.id}
                  sx={{ p: 2, display: "block", cursor: "pointer", "&:hover": { bgcolor: "rgba(0,0,0,0.04)" } }}
                  onClick={() => handleNotificationClick(n)}
                >
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography sx={{ fontWeight: "bold" }}>{n.title}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatNotificationTime(n.createdAt)}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {n.message}
                  </Typography>
                  <Divider sx={{ mt: 1.5 }} />
                </ListItem>
              ))
            )}
          </List>
        </Box>
      </Popover>

      {selectedServiceOrderId && (
        <NotificationReviewDialog
          open={reviewDialogOpen}
          onClose={() => {
            setReviewDialogOpen(false);
            setSelectedServiceOrderId(null);
          }}
          serviceOrderId={selectedServiceOrderId}
        />
      )}

      {selectedCommunicationId && (
        <NotificationCommunicationDialog
          open={communicationDialogOpen}
          onClose={() => {
            setCommunicationDialogOpen(false);
            setSelectedCommunicationId(null);
          }}
          communicationId={selectedCommunicationId}
        />
      )}
    </>
  );
}

export default HeaderNotifications;